import { fromRecipeList } from './fromRecipe'

export type AnalogRecipePath =
  | 'analog-layering-recipe'
  | 'analog-monolithic-recipe'

export const ANALOG_STORED_TYPE_BY_PATH: Record<AnalogRecipePath, string> = {
  'analog-layering-recipe': 'analog_layering',
  'analog-monolithic-recipe': 'analog_monolithic',
}

export const getAnalogRecipePath = (
  restorationMethod?: string | null,
): AnalogRecipePath =>
  restorationMethod === 'staining'
    ? 'analog-monolithic-recipe'
    : 'analog-layering-recipe'

export const getAnalogStoredType = (
  restorationMethod?: string | null,
): string => ANALOG_STORED_TYPE_BY_PATH[getAnalogRecipePath(restorationMethod)]

export const getAnalogStoredTypeFromPath = (recipePath: string): string =>
  ANALOG_STORED_TYPE_BY_PATH[recipePath as AnalogRecipePath] ??
  recipePath.replace(/-/g, '_')

const toSpaceNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined
  const n = Number(String(value).replace(/mm/gi, '').trim())
  return Number.isFinite(n) ? n : undefined
}

const assignIfPresent = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  if (value == null || value === '') return
  payload[key] = value
}

const assignSpace = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  const space = toSpaceNumber(value)
  if (space != null) payload[key] = space
}

const assignPair = (
  payload: Record<string, unknown>,
  key: string,
  pair?: unknown[] | null,
) => {
  if (!Array.isArray(pair)) return
  const filtered = pair.filter((item) => item != null && item !== '')
  if (!filtered.length) return
  payload[key] = pair
}

export const analogRecipeChart = (
  value: unknown,
): { labels: string[]; values: Array<number | string> } => {
  if (typeof value === 'string' || value == null) {
    return { labels: [], values: [] }
  }
  if (Array.isArray(value)) {
    const view = fromRecipeList(value)
    return { labels: view.names, values: view.ratios }
  }
  if (typeof value === 'object') {
    const recipe = value as {
      component_names?: string[]
      prep_names?: string[]
      powders?: string[]
      ratios?: Array<number | string>
      prep_ratios?: Array<number | string>
      simplified_ratios?: Array<number | string>
    }
    return {
      labels:
        recipe.component_names ?? recipe.prep_names ?? recipe.powders ?? [],
      values:
        recipe.ratios ?? recipe.prep_ratios ?? recipe.simplified_ratios ?? [],
    }
  }
  return { labels: [], values: [] }
}

type AnalogRegionValue<T> = {
  cervical?: T | null
  middle?: T | null
  incisal?: T | null
}

export const buildAnalogPayload = ({
  restorationMethod,
  recipeType,
  naturalDieMaterial,
  stainingbrand,
  ceramicbrand,
  frameworkColor,
  frameworkThickness,
  targetColors,
  prepColors,
  availspace,
}: {
  restorationMethod?: string | null
  recipeType?: string | null
  naturalDieMaterial?: string | null
  stainingbrand?: string | null
  ceramicbrand?: string | null
  frameworkColor?: unknown
  frameworkThickness?: unknown
  targetColors?: AnalogRegionValue<unknown[]>
  prepColors?: AnalogRegionValue<string>
  availspace?: AnalogRegionValue<unknown>
}): Record<string, unknown> => {
  const payload: Record<string, unknown> = {}
  const isTripleZone = recipeType === 'triple dentin'
  const isLayering = restorationMethod === 'layering'

  assignIfPresent(payload, 'recipe_type', recipeType)
  assignIfPresent(payload, 'natural_die_material', naturalDieMaterial)
  assignIfPresent(payload, 'stainingbrand', stainingbrand)
  assignIfPresent(payload, 'framework_color', frameworkColor)
  assignSpace(payload, 'framework_thickness', frameworkThickness)

  assignPair(payload, 'targetcolor_middle', targetColors?.middle)
  assignIfPresent(payload, 'prepcolor_middle', prepColors?.middle)

  if (isTripleZone) {
    assignPair(payload, 'targetcolor_cervical', targetColors?.cervical)
    assignPair(payload, 'targetcolor_incisal', targetColors?.incisal)
    assignIfPresent(payload, 'prepcolor_cervical', prepColors?.cervical)
    assignIfPresent(payload, 'prepcolor_incisal', prepColors?.incisal)
  }

  if (isLayering) {
    assignIfPresent(payload, 'ceramicbrand', ceramicbrand)
    assignSpace(payload, 'availspace_middle', availspace?.middle)
    if (isTripleZone) {
      assignSpace(payload, 'availspace_cervical', availspace?.cervical)
      assignSpace(payload, 'availspace_incisal', availspace?.incisal)
    }
  }

  return payload
}
